import { highlightBrowserLog } from '../helpers/log'
import { swiftCustomEventName } from '~/helpers/events'
import type { EcommerceEvents } from '~/hooks/useTrackEvents'
import type { Cart } from '~/type/Cart'

export const TrackEmarsysEvents = (
  push: (args: Record<string, any>) => void,
  options: {
    variableName: string
  },
) => {
  let notifications: any
  const { variableName } = options
  const windowObject = ((window as any) || {})[variableName]

  const subscribe = async (
    event: keyof typeof EcommerceEvents,
    data: {
      data: {
        ecommerData: any
        event: string
        cart?: Cart
      }
    },
  ) => {
    const cart = data?.data?.cart || data?.data?.ecommerData

    switch (true) {
      case event == 'viewCart': {
        sendCartTrack(
          cart?.items
            ?.map((item) => {
              return {
                item: item.productRefId || item.id,
                price: +(item.price / 100).toFixed(2),
                quantity: item.quantity,
              }
            })
            ?.sort((a, b) => {
              return a.item.localeCompare(b.item)
            }),
        )
        break
      }

      case event === 'beginCheckout': {
        initialize()
        checkSubscription()
        if (!cart) {
          highlightBrowserLog(`[WebEmarsysSdk viewStepCheckout()] -> cart not found`)
        }

        cart && customerDocument(cart)

        break
      }
      case event === 'viewStepCheckout': {
        if (!cart) {
          highlightBrowserLog(`[WebEmarsysSdk viewStepCheckout()] -> cart not found`)
        }

        cart && customerDocument(cart)
        sendTrack()

        break
      }
      case event === 'orderPlaced': {
        const orderId = new URLSearchParams(window.location.search).get('og')

        if (!orderId) {
          highlightBrowserLog(`[WebEmarsysSdk purchase()] -> orderId not found`)
          return
        }

        const cacheTransactionIdKey = 'order-transaction-id'
        const cacheTransactionIds = JSON.parse(localStorage.getItem(cacheTransactionIdKey) || '[]')

        // Garantir que cacheTransactionIds seja um array
        if (!Array.isArray(cacheTransactionIds)) {
          localStorage.setItem(cacheTransactionIdKey, JSON.stringify([]))
        }

        if (cacheTransactionIds.includes(orderId)) {
          console.log(`Order ${orderId} already tracked`, orderId)
          return
        }

        if (!(cart?.items).length) {
          highlightBrowserLog(`[WebEmarsysSdk purchase()] -> cart not found`, cart)
          return
        }

        const products = cart?.items?.map((item: any) => {
          return {
            item: item.productRefId || item.id,
            price: +((item.price * item.quantity) / 100).toFixed(2),
            quantity: item.quantity,
          }
        })
        const identifier = cart?.clientProfileData.document?.replace(/\D/g, '')

        await customerDocument(cart, identifier)

        const data = {
          orderId: orderId,
          items: products,
        }

        highlightBrowserLog(`[WebEmarsysSdk - purchase()] ->`, data)
        window.ScarabQueue.push(['purchase', data])

        const updatedCacheTransactionIds = [...cacheTransactionIds, orderId].slice(-8)

        localStorage.setItem(cacheTransactionIdKey, JSON.stringify(updatedCacheTransactionIds))

        break
      }
    }
  }

  // TODO: call always with the document already existing, one time for each page load
  async function customerDocument(orderForm: Cart, identifier?: string) {
    if (!identifier) {
      identifier = (orderForm?.clientProfileData?.document || '')?.replace(/\D/g, '') // document
    }

    if (identifier && identifier.length >= 11) {
      // 11 = length of CPF
      highlightBrowserLog(`[WebEmarsysSdk customerDocument()] ->`, identifier)

      window.ScarabQueue.push(['setCustomerId', identifier || ''])
      addUserToWebPush(identifier)
    } else {
      highlightBrowserLog(`[WebEmarsysSdk customerDocument()] -> 'EMPTY DOCUMENT 😱 or MASKED:`, identifier)
    }
    return true
  }

  const addUserToWebPush = async (document: any) => {
    if ((windowObject || {}).login && document) {
      // WebEmarsysSdk.login({ fieldId: 1259, fieldValue: document }) // hml
      windowObject
        .login({ fieldId: 9848, fieldValue: document }) // prd
        .then(function (success: boolean) {
          if (success) {
            highlightBrowserLog('User added to web push')
          } else {
            highlightBrowserLog('User not added to web push - error.')
          }
        })
        .catch(function (err: unknown) {
          highlightBrowserLog('User not added to web push - error', err)
        })
    }
  }

  const initialize = () => {
    push([
      'init',
      {
        enableLogging: window.location.hostname.includes('myvtex'),
        applicationCode: window?.EMARSYS_WEB_PUSH?.applicationCode,
        safariWebsitePushID: window?.EMARSYS_WEB_PUSH?.safariWebsitePushID,
        enableMacSafariVapid: true,
        safariPushPackageServiceUrl: '',
        defaultNotificationTitle: 'Swift.com.br',
        defaultNotificationIcon: 'https://swift-static.online.pro.br/arquivos/2024-logo-swift-primary.svg',
        autoSubscribe: false,
        serviceWorker: {
          url: 'files/service-worker.js',
          applicationServerPublicKey: window?.EMARSYS_WEB_PUSH?.applicationServerPublicKey,
        },
      },
    ])
  }

  const checkSubscription = async () => {
    // verifica se existe a função
    if (windowObject?.isSubscribed) {
      windowObject?.isSubscribed().then((isSubscribed: boolean) => {
        // if (window.EMARSYS_WEB_PUSH.autoSubscribe) {
        if (typeof isSubscribed !== 'boolean') {
          // sem informações sobre a inscrição, vamos mostrar o modal
          notifications?.init()
          notifications?.autoModal()
        } else if (isSubscribed === false) {
          // usuário não está inscrito
          notifications?.init()
        } else if (isSubscribed === true) {
          // usuário está inscrito
          windowObject?.getLoggedInContact().then(function (res: any) {
            if (res && Object.keys(res).length !== 0) {
              // customer identified
              highlightBrowserLog('Customer identified:', res)
            } else {
              // customer not identified
              highlightBrowserLog('Customer not identified')
            }
          })
        }
        // }
        highlightBrowserLog('Status subscribed web push:', isSubscribed)
      })
    } else {
      highlightBrowserLog(`WebEmarsysSdk?.isSubscribed not found`)
    }
  }

  function sendTrack() {
    highlightBrowserLog('sendTrack()')
    window.ScarabQueue.push(['go'])
  }

  async function sendCartTrack(products: any) {
    highlightBrowserLog('cart()', products)
    window.ScarabQueue.push(['cart', products])
  }

  document.addEventListener(swiftCustomEventName, (e: any) => {
    subscribe(e.detail.name, e.detail)
  })

  window.addEventListener('load', checkSubscription)

  window.requestEmarsysNotificationPermission = async () => {
    return new Promise((resolve, reject) => {
      try {
        if (!windowObject) {
          highlightBrowserLog('WebEmarsysSdk not found')
          reject()
          return
        }

        if (!('Notification' in window)) {
          highlightBrowserLog('Notification not supported')
          reject()
          return
        }

        if (Notification.permission === 'granted') {
          highlightBrowserLog('User already granted permission to web push')
          windowObject.subscribe()
          resolve()
        } else if (Notification.permission !== 'denied') {
          highlightBrowserLog('Requesting permission to web push...')
          Notification.requestPermission().then((permission) => {
            highlightBrowserLog('Permission to web push:', permission)
            if (permission === 'granted') {
              windowObject.subscribe()
              resolve()
            } else {
              reject()
            }
          })
        }
      } catch (e) {
        highlightBrowserLog('Error on requestEmarsysNotificationPermission', e)
        reject(e)
      }
    })
  }
}
